import { autorun, makeAutoObservable } from 'mobx';
import { getChipSelectability } from '@/utils/chipPlacement';
import type {
  IChipModel,
  IEditorModel,
  IPlaceChipModel,
  ITileManagerModel,
} from '@/interfaces/models';
import Context from '@/context';
import { Rotation, rotateCW, rotateCCW } from '@/models/data/Rotation';
import type { TileInstance } from '@/models/tiles/TileInstance';

export class PlaceChipModel implements IPlaceChipModel {
  selectedChip: IChipModel | null = null;
  chipRotation: Rotation = Rotation.CW_0;
  fromTilePalette = false;
  filter = '';
  placementMessage: string | null = null;
  // Paint-only cells outside the authoring range never enter a TileManager.
  overflowPreviewTiles: TileInstance[] = [];

  constructor(
    public previewTileManagerModel: ITileManagerModel,
    private context: Context,
    private editorModel?: IEditorModel,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.setupReactions();
  }

  selectChip(chip: IChipModel) {
    if (!this.canSelectChip(chip)) {
      return;
    }

    this.selectedChip = chip;
  }

  getSelectability(chip: IChipModel): { allowed: boolean; reason?: string } {
    if (chip.templateType)
      return { allowed: false, reason: 'Select this arrangement from Templates.' };
    if (!this.editorModel) {
      return { allowed: false, reason: 'Editor not initialized' };
    }

    return getChipSelectability(chip, this.editorModel.currentChip, this.context);
  }

  canSelectChip(chip: IChipModel): boolean {
    return this.getSelectability(chip).allowed;
  }

  bindEditor(editorModel: IEditorModel) {
    this.editorModel = editorModel;
  }

  rotateChipCW() {
    this.chipRotation = rotateCW(this.chipRotation);
  }

  rotateChipCCW() {
    this.chipRotation = rotateCCW(this.chipRotation);
  }

  setFilter(filter: string) {
    this.filter = filter;
  }

  private setupReactions() {
    autorun(() => {
      if (this.selectedChip === null) {
        return;
      }

      const stillExists = this.context.chipStore.chips.some(
        (chip) => chip.id === this.selectedChip?.id,
      );

      // Keep an existing ghost when its source becomes temporarily invalid.
      // Placement validation explains the reason and still rejects the edit.
      if (!stillExists) {
        this.selectedChip = null;
      }
    });
  }

  get selectableChips() {
    if (!this.editorModel) {
      return [];
    }

    // Ordinary projects keep invalid definitions visible for repair. A solver
    // palette only advertises chips its immutable challenge actually permits.
    const query = this.filter.trim().toLowerCase();
    return this.context.chipStore.chips.filter(
      (chip) =>
        !chip.templateType &&
        (!this.context.challenge ||
          (!!this.context.challenge.playerChips &&
            chip.id !== this.context.mainChip.id) ||
          this.canSelectChip(chip)) &&
        (chip.name.toLowerCase().includes(query) ||
          (chip.folder ?? '').toLowerCase().includes(query)),
    );
  }
}
